/**
 * Focusable Widget Class
 * --------------------------------------------------------------
 * @summary High-level general Class for creating focusable widgets
 * (drawers, modals, etc). Provides css-based open/close/toggle
 * functionality, as well as programatically manipulation of the widget,
 * and handles a11y.
 *
 * @usage class widget extends FocusableWidget {...}
 *
 * @emits focusable-widget:open on document
 * @emits focusable-widget:close on document
 * @emits <config.name>:open on document
 * @emits <config.name>:close on document
 * @emits <#id>:open on document
 * @emits <#id>:close on document
 * @emits open on element
 * @emits close on element
 *
 * The following parameters are attributes that any children of FocusableWidget
 * element accepts
 * @param id - required
 * @param open - Query Selector to all the widget's open triggers
 * @param close - Query Selector to all the widget's close triggers
 * @param [open-class] - Optional class to add to widget when opened
 * @param [body-open-class] - Optional class to add to body when opened
 * @param [stackable] - Optional attribute. If present, widget will not close other widgets when opening (allows stacking)
 * @class
 */
class FocusableWidget extends HTMLElement {
  /**
   * Creates Focusable Widget
   * @param {Object} config - Configuration object
   * @param {String} config.name - widget name, used for classnames and for providing useful error messages
   * @param {String} config.background - query selector to widget background
   * @param {Array} [config.widgetOpenClasses] - optional classes to add to widget when opened
   * @param {Array} [config.bodyOpenClasses] - optional classes to add to body when widget is opened
   * @param {Object} [config.attributes] - optional attributes to add to element upon initialization.
   *                                       example: {'data-custom-attr': true }
   */
  constructor(config = {}) {
    super();

    if (!config.name)
      throw new Error(`Focusable Widget(${config.name}) must have a name`);
    if (!this.id) throw new Error(`${config.name} must have an id`);
    if (!config.background)
      throw new Error(
        `Focusable Widget(${config.name}) must have a background`
      );

    /**
     * Save client config for reinit method
     * @private
     */
    this._clientConfig = config;

    this._init(config);
  }

  /**
   * Opens widget. If already opened, does nothing.
   * @usage document.querySelector('[js-my-widget]').open()
   * @returns {HTMLElement} widget
   * @public
   */
  open = () => {
    if (this._isOpen) return;
    // Only close other widgets if this widget is not stackable
    if (!this._isStackable) {
      this._closeFocusableWidgets();
    }
    const result = this.toggle();

    if (this._isOpen && typeof this._config.openCallBack === 'function') {
      this._config.openCallBack();
    }

    return result
  }

  /**
   * Closes widget. If already closed, does nothing.
   * @usage document.querySelector('[js-my-widget]').close()
   * @returns {HTMLElement} widget
   * @public
   */
  close = () => {
    if (!this._isOpen) return;
    const result = this.toggle();

    if (!this._isOpen && typeof this._config.closeCallBack === 'function') {
      this._config.closeCallBack();
    }

    return result;
  }

  /**
   * Toggles widget.
   * @usage document.querySelector('[js-my-widget]').toggle()
   * @returns {HTMLElement} widget
   * @public
   */
  toggle = () => {
    // theme.utils.prepareTransition(this);
    this._toggleClasses();
    this._toggleAccessibilityAttributes();

    const wasOpen = this._isOpen;
    this._isOpen = !this._isOpen;

    if (!wasOpen) {
      // Opening
      // theme.utils.a11y.trapFocus(
      //   this
      // );
      this._bindEvents();
      this._dispatchEvents(this._events.open, ['open']);
    } else {
      // Closing
      // theme.utils.a11y.removeTrapFocus(this);
      this._unbindEvents();
      this._dispatchEvents(this._events.close, ['close']);
    }

    // Update background z-index if this is a stackable widget
    if (this._isStackable) {
      this._updateStackableBackground();
    }

    return this;
  }

  /**
   * Reinitializes the widget
   * @usage document.querySelector('[js-my-widget]').reinitWidget()
   * @public
   */
  reinitWidget = () => {
    this._init(this._clientConfig);
  }

  /**
   * @usage const open = document.querySelector('[js-my-widget]').isOpen;
   * @type {Boolean}
   * @public
   */
  get isOpen() {
    return this._isOpen;
  }

  // ======================================================== PRIVATE METHODS

  /**
   * Initializes entire logic and state
   * @param {Object} config - constructor config param
   * @private
   */
  _init({
    name,
    background,
    widgetOpenClasses = [],
    bodyOpenClasses = [],
    attributes = {},
  }) {
    /**
     * Widget name
     * @type {String} @private
     * */
    this._name = name;

    /** @type {Boolean} @private */
    this._isOpen = false;

    /**
     * Whether this widget can stack on top of other widgets without closing them
     * @type {Boolean} @private
     */
    this._isStackable = this.getAttribute("stackable") !== null;

    /**
     * @property {String} background - widget background selector
     * @property {String} open - selector to all open triggers
     * @property {String} close - selector to all close triggers
     * @private
     */
    this._selectors = {
      background,
      open: this.getAttribute("open"),
      close: this.getAttribute("close"),
      onOpen: this.getAttribute("onOpen"),
      onClose: this.getAttribute("onClose"),
    };

    /**
     * Nodes the widget depends on
     * @property {HTMLElement} background - widget background
     * @private
     */
    this._nodes = {
      background: document.querySelector(this._selectors.background),
    };

    /**
     * Widget events fired on actions to document
     * @private
     */
    this._events = {
      open: ["focusable-widget:open", `${name}:open`, `${this.id}:open`],
      close: ["focusable-widget:close", `${name}:close`, `${this.id}:close`],
    };

    /**
     * Private Widget configuration
     * @property {HTMLAllCollection} open - open triggers
     * @property {HTMLAllCollection} close - close triggers
     * @property {Array} attributes - attributes added to widget upon initialization
     * @property {Array} widgetOpenClasses - classes added to widget when opened
     * @property {String} bodyOpenClasses - classes added to body when widget opened
     * @private
     */
    this._config = {
      open: document.querySelectorAll(this._selectors.open),
      close: document.querySelectorAll(this._selectors.close),
      attributes: {
        "data-focusable-widget": "true",
        "aria-hidden": "true",
        tabindex: 0,
        ...attributes,
      },
      widgetOpenClasses: ["open", this.getAttribute("open-class")].concat(
        widgetOpenClasses
      ),
      bodyOpenClasses: [
        "js-focusable-widget-open",
        `js-${name}-open`,
        this.getAttribute("body-open-class"),
      ].concat(bodyOpenClasses),
      openCallBack: this._selectors.onOpen ? () => {window[this._selectors.onOpen]()} : null,
      closeCallBack: this._selectors.onClose ? () => {window[this._selectors.onClose]()} : null
    };

    this._validateConfig();
    this._addAccessibilityAttributes();
    this._setupListeners();

    this.classList.add("hydrated");

    if (this.hasAttribute("hidden")) {
      this.removeAttribute("hidden");
    }

  }

  /**
   * Validates widget config, throwing useful errors if invalid
   * @private
   */
  _validateConfig() {
    if (!this._config.open || !this._config.open.length)
      console.warn(`WARN: ${this._name}: no open triggers found. Check your ${this._name} 'open' attribute, or add a trigger (if needed)`)

    if (!this._config.close || !this._config.close.length)
      throw new Error(
        `${this._name}: invalid close trigger selector, no elements found. Check your ${this._name} 'close' attribute`
      );
  }

  /**
   * Setups initialization event listeners
   * @private
   */
  _setupListeners() {
    this._config.open.forEach((open) =>
      open.addEventListener("click", this._open.bind(this))
    );
    this._config.close.forEach((close) =>
      close.addEventListener("click", this._close.bind(this))
    );
    this.addEventListener("click", (e) => e.stopPropagation());
  }

  /**
   * Internal open wrapper, to keep our public methods clean, internally deals with event object
   * @param {object} event
   * @private
   */
  _open(event) {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    this.open();
  }

  /**
   * Internal close wrapper, to keep our public methods clean, internally deals with event object
   * @param {object} event
   * @private
   */
  _close = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.close();
  }

  /**
   * Closes any open focusable widgets
   * @private
   */
  _closeFocusableWidgets() {
    const openWidgets = document.querySelectorAll(
      "[data-focusable-widget].open"
    );
    openWidgets.forEach((widget) => widget.close());
  }

  /**
   * Updates background overlay z-index when stackable drawers are open
   * Adds/removes class to body to control background z-index
   * @private
   */
  _updateStackableBackground() {
    const stackableOpen = document.querySelectorAll(
      "[data-focusable-widget].open[stackable]"
    );
    
    if (stackableOpen.length > 0) {
      document.body.classList.add("js-stackable-drawer-open");
    } else {
      document.body.classList.remove("js-stackable-drawer-open");
    }
  }

  /**
   * Dispatches an array of custom events on document
   * @param {Array} events - this._events.<event>
   */
  _dispatchEvents(globalEvents, elementEvents) {
    globalEvents.forEach(e => document.dispatchEvent(new CustomEvent(e)))
    if (elementEvents && elementEvents.length) 
    elementEvents.forEach(e => this.dispatchEvent(new CustomEvent(e)))
  }

  /**
   * Binds events needed for opened widget
   * @private
   */
  _bindEvents() {
    document.addEventListener("keyup", this._onKeyUp);
    this._nodes.background.addEventListener("click", this._onBackgroundClick);
    this._nodes.background.addEventListener("touchmove", this._onTouchMove);
  }

  /**
   * Unbinds events added when widget was opened
   * @private
   */
  _unbindEvents() {
    document.removeEventListener("keyup", this._onKeyUp);
    this._nodes.background.removeEventListener("click", this._onBackgroundClick);
    this._nodes.background.removeEventListener(
      "touchmove",
      this._onTouchMove
    );
  }

  /**
   * Toggles classes based on current widget state
   * @private
   */
  _toggleClasses() {
    if (this._isOpen) {
      // Body classes
      this._config.bodyOpenClasses.forEach((className) => {
        if (!className) return;
        document.body.classList.remove(className);
      });

      // Widget Classes
      this._config.widgetOpenClasses.forEach((className) => {
        if (!className) return;
        this.classList.remove(className);
      });
    } else {
      // Body classes
      this._config.bodyOpenClasses.forEach((className) => {
        if (!className) return;
        document.body.classList.add(className);
      });

      // Widget Classes
      this._config.widgetOpenClasses.forEach((className) => {
        if (!className) return;
        this.classList.add(className);
      });
    }
  }

  /**
   * Toggles a11y attributes based on current widget state
   * @private
   */
  _toggleAccessibilityAttributes() {
    if (this._isOpen) {
      this.setAttribute("aria-hidden", "true");
      this.setAttribute("tabindex", "-1");
      this._config.open.forEach((open) =>
        open.setAttribute("aria-expanded", "false")
      );
      this._config.close.forEach((close) =>
        close.setAttribute("aria-expanded", "false")
      );
    } else {
      this.setAttribute("aria-hidden", "false");
      this.setAttribute("tabindex", "0");
      this._config.open.forEach((open) =>
        open.setAttribute("aria-expanded", "true")
      );
      this._config.close.forEach((close) =>
        close.setAttribute("aria-expanded", "true")
      );
    }
  }

  /**
   * Adds a11y attributes to widget and triggers
   * @private
   */
  _addAccessibilityAttributes() {
    const addA11yAttributes = (el) => {
      el.setAttribute("tabindex", "0");
      el.setAttribute("aria-expanded", "false");
      el.setAttribute("aria-controls", this.id);
    };

    for (const attr in this._config.attributes) {
      this.setAttribute(attr, this._config.attributes[attr]);
    }
    this._config.open.forEach(addA11yAttributes);
    this._config.close.forEach(addA11yAttributes);
  }

  /**
   * Handles background click - closes stackable drawer if open, otherwise closes this widget
   * @param {object} event
   * @private
   */
  _onBackgroundClick = (event) => {
    // Only handle clicks directly on the background element itself (the overlay area)
    // Ignore clicks on children (page content)
    if (event.target !== this._nodes.background) {
      return;
    }

    // Check if there are any stackable drawers open
    const stackableOpen = document.querySelectorAll(
      "[data-focusable-widget].open[stackable]"
    );
    
    if (stackableOpen.length > 0) {
      // If stackable drawers are open, close the topmost one (last in DOM order)
      const topStackable = stackableOpen[stackableOpen.length - 1];
      if (topStackable && topStackable.close) {
        event.preventDefault();
        event.stopPropagation();
        topStackable.close();
      }
    } else {
      // No stackable drawers open, close this widget
      this._close(event);
    }
  }

  /**
   * Closes widget on esc key press
   * @param {object} event
   * @private
   */
  _onKeyUp = (event) => {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    this.close();
  }

  /**
   * Lock scrolling on mobile
   * @returns {Boolean}
   * @private
   */
  _onTouchMove = () => {
    return false;
  }
}


class Drawer extends FocusableWidget {
  constructor() {
    super({
      name: "drawer",
      background: ".drawer-background"
    });

    const position = this.getAttribute("position");
    if (!position) throw new Error("drawer must have position attribute");

    /** @type {string} @private */
    this._position = position;

    // Add position class to body when drawer opens
    this._config.bodyOpenClasses.push(`js-drawer-${position}-open`);

    // This makes it persist if the widget gets re-initialized
    this._clientConfig.bodyOpenClasses = [`js-drawer-${position}-open`]
    this._preservedUnderCartWidgets = []
  }

  open = () => {
    if (this._isOpen) return;

    if (!this._isStackable) {
      if (this._isCartDrawer()) {
        this._preservedUnderCartWidgets = this._closeFocusableWidgetsForCart();
      } else {
        this._closeFocusableWidgets();
      }
    }

    const result = this.toggle();

    if (this._isOpen && this._isCartDrawer()) {
      this._setWidgetsUnderCartState(true);
    }

    if (this._isOpen && typeof this._config.openCallBack === 'function') {
      this._config.openCallBack();
    }

    return result
  }

  close = () => {
    if (!this._isOpen) return;

    const result = this.toggle();

    if (!this._isOpen) {
      // Always re-sync body classes so any still-open drawers underneath keep their shadow/overlay
      this._syncBodyOpenClassesFromActiveWidgets();
    }

    if (!this._isOpen && this._isCartDrawer()) {
      this._setWidgetsUnderCartState(false);
      this._preservedUnderCartWidgets = [];
    }

    if (!this._isOpen && typeof this._config.closeCallBack === 'function') {
      this._config.closeCallBack();
    }

    return result;
  }

  _isCartDrawer() {
    return this.id === "CartDrawer";
  }

  _closeFocusableWidgetsForCart() {
    const preservedWidgets = [];
    const openWidgets = document.querySelectorAll("[data-focusable-widget].open");

    openWidgets.forEach((widget) => {
      if (widget === this) return;

      if (widget.hasAttribute("data-preserve-under-cart")) {
        preservedWidgets.push(widget);
        return;
      }

      if (widget.close) {
        widget.close();
      }
    });

    return preservedWidgets;
  }

  _setWidgetsUnderCartState(isUnderCart) {
    if (!this._preservedUnderCartWidgets.length) return;

    this._preservedUnderCartWidgets.forEach((widget) => {
      if (!widget || !widget.classList.contains("open")) return;

      if (isUnderCart) {
        if (typeof widget._unbindEvents === "function") {
          widget._unbindEvents();
        }
        widget.setAttribute("aria-hidden", "true");
        widget.setAttribute("tabindex", "-1");
        return;
      }

      widget.setAttribute("aria-hidden", "false");
      widget.setAttribute("tabindex", "0");

      if (typeof widget._bindEvents === "function") {
        widget._bindEvents();
      }
    });

    if (!isUnderCart) {
      const widgetToFocus = this._preservedUnderCartWidgets.find(
        (widget) => widget && widget.classList.contains("open")
      );

      if (widgetToFocus) {
        theme.utils.a11y.trapFocus(widgetToFocus);
      }
    }
  }

  _syncBodyOpenClassesFromActiveWidgets() {
    const openWidgets = document.querySelectorAll("[data-focusable-widget].open");

    if (!openWidgets.length) return;

    document.body.classList.add("js-focusable-widget-open");

    openWidgets.forEach((widget) => {
      if (widget.tagName === "S-DRAWER") {
        document.body.classList.add("js-drawer-open");
        const position = widget.getAttribute("position");
        if (position) {
          document.body.classList.add(`js-drawer-${position}-open`);
        }
      }

      const bodyOpenClass = widget.getAttribute("body-open-class");
      if (bodyOpenClass) {
        document.body.classList.add(bodyOpenClass);
      }
    });
  }
  /**
   * Drawer position: 'top', 'right', 'bottom', 'left'
   * @usage const position = document.querySelector('[js-my-drawer]').position;
   * @type {string}
   * @public
   */
  get position() {
    return this._position;
  }
}

customElements.define("s-drawer", Drawer);


/**
 * Modal Component
 * --------------------------------------------------------------------
 * @summary modal custom element component, must have an id and
 * specified open/close triggers. There must be a close trigger inside
 * the modal container.
 *
 * See parent focusable-widget.js events and public methods for complete documentation.
 *
 * @usage
 * <s-modal id="MyModal" class="{{styling classes}}" open="[open-my-modal]" close="[close-my-modal]">
 *   CONTENTS, make sure to include a close button in here.
 * </s-modal>
 * @usage to manipulate programatically: document.querySelector('#MyModal').open();
 *
 * @emits modal:open on document
 * @emits modal:close on document
 * @emits <#id>:open on document
 * @emits <#id>:close on document
 * @emits open on element
 * @emits close on element
 * 
 * The following parameters are attributes that the <modal> element takes
 * @param id - required
 * @param open - Query Selector to all the drawer's open triggers
 * @param close - Query Selector to all the drawer's close triggers
 * @param [open-class] - Optional class to add to drawer when opened
 * @param [body-open-class] - Optional class to add to body when opened
 */
class Modal extends FocusableWidget {
  constructor() {
    super({
      name: "modal",
      background: ".modal-background",
      bodyOpenClasses: 'js-modal-open'
    });
  }
}

customElements.define("s-modal", Modal);
